// Which rate cards price a collection, which seasons apply, and what add-ons are offered.
import {
  offeringBranches,
  preferLine,
  type PricingAddOn,
  type PricingCard,
  type PricingPromotion,
  type ServiceLine,
} from '@ecobox/domain';
import type { ConfigRecord } from '@ecobox/schema';
import { toPricingCard } from './rate-card';

export type RateCard = ConfigRecord<'rateCard'>;
export type Season = ConfigRecord<'season'>;
export type Promotion = ConfigRecord<'promotion'>;

/**
 * The promotions a device prices with (03 §3.3): the active ones whose dates have not passed. The
 * domain `quote` picks the one that applies to the sale, exactly as the server does.
 */
export function livePromotions(records: readonly Promotion[], today: string): PricingPromotion[] {
  return records
    .filter((r) => r.status === 'active' && r.endsOn >= today)
    .map((r) => ({
      id: r.id,
      name: r.name,
      ...(r.label ? { label: r.label } : {}),
      status: r.status,
      ...(r.percentOff === undefined ? {} : { percentOff: r.percentOff }),
      ...(r.amountOffMinor === undefined ? {} : { amountOffMinor: r.amountOffMinor }),
      startsOn: r.startsOn,
      endsOn: r.endsOn,
      originSectorIds: r.originSectorIds,
      originBranchIds: r.originBranchIds,
      services: r.services,
      serviceLines: r.serviceLines,
      destinationCountries: r.destinationCountries,
      sizeIds: r.sizeIds,
    }));
}

function newest(cards: readonly RateCard[]): RateCard | undefined {
  return [...cards].sort(
    (a, b) => b.version - a.version || b.effectiveFrom.localeCompare(a.effectiveFrom),
  )[0];
}

/**
 * The cards that price a shipment, in precedence order (03 §3.3): the branch's own active card
 * for the service first, then its parent hub's when it is a satellite (2026-10-29), then the
 * country's. A card is usable on `today` when it is active and inside its effective dates.
 */
export function selectPricingCards(
  records: readonly RateCard[],
  args: {
    branchId: string;
    /** The branch's parent hub when it is a satellite: its cards price after the branch's own. */
    parentHubId?: string | undefined;
    sectorId: string | undefined;
    service: string;
    /** When set, a card priced for this line is preferred; else the match on `service` stands. */
    serviceLine?: ServiceLine | undefined;
    today: string;
  },
): PricingCard[] {
  const live = records.filter(
    (r) =>
      r.status === 'active' &&
      r.service === args.service &&
      r.effectiveFrom <= args.today &&
      (r.effectiveTo === undefined || r.effectiveTo >= args.today),
  );
  const picked = [
    ...offeringBranches({ code: args.branchId, parentHubId: args.parentHubId }).map((code) =>
      newest(
        preferLine(
          live.filter((r) => r.originBranchId === code),
          args.serviceLine,
        ),
      ),
    ),
    args.sectorId === undefined
      ? undefined
      : newest(
          preferLine(
            live.filter((r) => r.originSectorId === args.sectorId),
            args.serviceLine,
          ),
        ),
  ];
  return picked.flatMap((r) => {
    const card = r ? toPricingCard(r as unknown as Record<string, unknown>) : undefined;
    return card ? [card] : [];
  });
}

/** Add-ons come from the card that prices the first box (domain `quote`). */
export function offeredAddOns(cards: readonly PricingCard[]): readonly PricingAddOn[] {
  return cards[0]?.addOns ?? [];
}

/** Active seasons whose booking window contains today (13 §13.4 auto tag). */
export function suggestSeasons(seasons: readonly Season[], today: string): string[] {
  return seasons
    .filter(
      (s) =>
        s.active &&
        s.autoTag !== undefined &&
        s.autoTag.bookingFrom <= today &&
        today <= s.autoTag.bookingTo,
    )
    .map((s) => s.id);
}
